import {  Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IHeadEnrolment,
} from "models/workshop";
import React, { useState } from "react";
import { QueryClient, useMutation } from "react-query";
import BlockIcon from "@mui/icons-material/Block";
import {
  getEnrollments,
  HeadEnrollmentsCells,
  respondToEnrollment,
  WorkshopCells
} from "framework/workshops";
import { getPermission, getUserData } from "helpers";
import { handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserType } from "types/auth";
import List from "components/UI/List";
import { divIcon } from "leaflet";
import ApproveEnrollment from "./partials/ApproveEnrollment";
import RejectEnrollment from "./partials/RejectEnrollment";


interface Props {
  feature: Feature;
}

interface EnrollmentStatus {id:string,status:boolean}

const HeadRoleWorkShop: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [selectedEnrollment,setSelectedEnrollment]=useState<EnrollmentStatus>({} as EnrollmentStatus)
  const [loading2, setLoading2] = useState<boolean>(false);
  const [openApprove, setOpenApprove] = useState<boolean>(false);
  const [openReject, setOpenReject] = useState<boolean>(false);
  const userData = getUserData();
  const { colors, user } = userData;
  const userType = user.userType;

  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();



  const respondEnrolmentMutation = useMutation({
    mutationFn: (enrollment: EnrollmentStatus) => {
      const {id,status}=enrollment
      return respondToEnrollment(id,status);
    },
  });



  const {
    loading,
    data: enrollment,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getEnrollments(),
    fetchPermission: () => getPermission(6),
    section: "Workshops",
  });



  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );






  const handleOpenApprove = (enrollment: IHeadEnrolment) => {
    setSelectedEnrollment({id:enrollment.enrollmentId,status:true})
    setOpenApprove(true);
  };


  const handleOpenReject = (enrollment: IHeadEnrolment) => {
    setSelectedEnrollment({id:enrollment.enrollmentId,status:false})
    setOpenReject(true);
  };

  const handleCloseModal =()=>{
    setSelectedEnrollment({} as EnrollmentStatus)
    setOpenApprove(false);
    setOpenReject(false);
  }


  const getBodyCells = (row: IHeadEnrolment) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "workshopTitle")
        return {
          id: "workshopTitle",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.workshopTitle}
              </Typography>
            </TableCell>
          ),
        };
      // if (key === "enrollmentStatus")
      //   return {
      //     id: "enrollmentStatus",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">
      //           { row.enrollmentStatus}
      //         </Typography>
      //       </TableCell>
      //     ),
      //   };

      if (key === "employeeName")
        return {
          id: "employeeName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.employeeName}
              </Typography>
            </TableCell>
          ),
        };

     

      if (key === "workshopId")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {row.enrollmentStatus && row.enrollmentStatus.toLowerCase() !== 'pending' ? <Typography variant="body2" sx={{ color: "#888" }}>
                      {row.enrollmentStatus}
                    </Typography> : <div style={{display:'flex',gap:'10px'}}>
                  {permission?.hasModifyPermission && (
                  <Button onClick={() => handleOpenApprove(row)} variant="contained" color="success">{t('T_approve')}</Button>
                )}

                {permission?.hasModifyPermission && (
                 <Button onClick={() => handleOpenReject(row)} variant="contained" color="error">{t('T_reject')}</Button>
                )}
                  </div>}
               

            
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };


  const handleRespond = async () => {
    try {
      setLoading2(true);
      await respondEnrolmentMutation.mutateAsync(selectedEnrollment);
      setAlert({
        open: true,
        message: "successful",
        type: "success",
      });
      revalidate();
      handleCloseModal();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };



  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            spacing={1}
            width={"100%"}
            marginBottom={5}
          >
            <Typography
              variant="h3"
              color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            >
              {t(feature.name)}
            </Typography>
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && enrollment!.length > 0 && (
          <List
            headCells={HeadEnrollmentsCells}
            data={enrollment!}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!enrollment || enrollment.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_workshop_header")}
              message={t("T_no_workshop_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>



      {openApprove && (
        <ApproveEnrollment
          open={openApprove}
          loading={loading2}
          onClose={handleCloseModal}
          onSubmit={handleRespond}
        />
      )}

      {openReject && (
        <RejectEnrollment
          open={openReject}
          loading={loading2}
          onClose={handleCloseModal}
          onSubmit={handleRespond}
        />
      )}

    
    </>
  );
};

export default HeadRoleWorkShop;
